/*
  Customer Reviews Gallery - marquee engine.

  Seamless looping is achieved by modular arithmetic rather than by repositioning
  nodes: the original cards are cloned until the track is at least one loop wider
  than the viewport, then the scroll offset is wrapped with `offset % loopWidth`
  every frame. Because the content repeats exactly every `loopWidth`, the wrap is
  invisible - there is no jump, flicker or reset for the visitor to notice.

  Opening the lightbox is NOT this element's job - `review-gallery-viewer.js`
  owns that for every review layout. This element only listens for the two
  events that viewer bubbles, so the marquee holds still while a review is open.

  The `if (!customElements.get(...))` guard is required: `theme-editor.js`
  re-injects section `<script src>` tags on every `shopify:section:load`.
*/
if (!customElements.get('m-review-gallery')) {
  const DRAG_THRESHOLD_PX = 6;
  const CLICK_SUPPRESS_PX = 4;
  const CLICK_SUPPRESS_MS = 320;
  const MAX_FRAME_MS = 50;
  const REFERENCE_FRAME_MS = 1000 / 60;
  const FRICTION_PER_FRAME = 0.94;
  const VELOCITY_WINDOW_MS = 90;
  const MAX_FLICK_PX_PER_MS = 3;
  const MIN_FLICK_PX_PER_MS = 0.02;
  const TOUCH_RESUME_DELAY_MS = 1500;
  const MAX_LOOP_COPIES = 20;
  const FOCUS_EDGE_PADDING_PX = 16;
  const WHEEL_LINE_HEIGHT_PX = 16;

  /**
   * Marks a cloned card as invisible to assistive technology and unreachable by
   * Tab, so every review is announced and focusable exactly once.
   *
   * Note it is deliberately NOT `inert`: inert content cannot be clicked, and on
   * a wide screen most visible cards are clones - marking them inert made the
   * lightbox unreachable for all of them. `review-gallery-viewer.js` resolves a
   * clicked clone back to its original before restoring focus, so nothing ever
   * ends up focused inside this `aria-hidden` subtree.
   * @param {HTMLElement} clone
   */
  function neutraliseClone(clone) {
    clone.classList.add('is-clone');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a, button, [tabindex]').forEach((node) => {
      node.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Normalises a wheel delta to pixels regardless of the reported delta mode.
   * @param {WheelEvent} event
   * @param {number} viewportWidth
   * @returns {number}
   */
  function wheelDeltaToPixels(event, viewportWidth) {
    if (event.deltaMode === 1) return event.deltaX * WHEEL_LINE_HEIGHT_PX;
    if (event.deltaMode === 2) return event.deltaX * viewportWidth;
    return event.deltaX;
  }

  class ReviewGallery extends HTMLElement {
    constructor() {
      super();

      this.offset = 0;
      this.momentum = 0;
      this.loopWidth = 0;
      this.frameId = null;
      this.lastFrameTime = 0;
      this.isMarquee = false;
      this.isDragging = false;
      this.isPointerDown = false;
      this.pointerId = null;
      this.dragDistance = 0;
      this.lastPointerX = 0;
      this.suppressClickUntil = 0;
      this.samples = [];
      this.pauseReasons = new Set();
      this.touchResumeTimer = null;
      this.resizeFrameId = null;

      this.tick = this.tick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onViewerOpen = this.onViewerOpen.bind(this);
      this.onViewerClose = this.onViewerClose.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.m-review-gallery__viewport');
      this.track = this.querySelector('.m-review-gallery__track');
      if (!this.viewport || !this.track) return;

      this.originals = Array.from(this.track.children);
      if (!this.originals.length) return;

      this.autoScrollEnabled = this.dataset.autoscroll === 'true';
      this.speed = Math.max(0, parseFloat(this.dataset.speed) || 0) / 1000;
      this.directionSign = this.dataset.direction === 'right' ? -1 : 1;
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.pauseOnTouch = this.dataset.pauseTouch === 'true';

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverQuery = window.matchMedia('(hover: hover)');
      this.motionQuery.addEventListener('change', this.onMotionPreferenceChange);

      // Emitted by the nested `m-review-viewer`; the marquee must hold still
      // while a review is open full screen.
      this.addEventListener('rg:viewer-open', this.onViewerOpen);
      this.addEventListener('rg:viewer-close', this.onViewerClose);

      if (this.motionQuery.matches) {
        this.enableStaticMode();
      } else {
        this.enableMarqueeMode();
      }
    }

    disconnectedCallback() {
      this.stopLoop();
      this.teardownMarqueeListeners();
      this.motionQuery?.removeEventListener('change', this.onMotionPreferenceChange);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('rg:viewer-open', this.onViewerOpen);
      this.removeEventListener('rg:viewer-close', this.onViewerClose);
      if (this.resizeFrameId) cancelAnimationFrame(this.resizeFrameId);
      if (this.touchResumeTimer) clearTimeout(this.touchResumeTimer);
    }

    /* ----------------------------------------------------------------- modes */

    /** Native horizontal scrolling. Used when the visitor prefers reduced motion. */
    enableStaticMode() {
      this.stopLoop();
      this.teardownMarqueeListeners();
      this.removeClones();
      this.isMarquee = false;
      this.offset = 0;
      this.track.style.transform = '';
      this.classList.remove('is-marquee', 'is-running');
    }

    enableMarqueeMode() {
      if (this.isMarquee) return;
      this.isMarquee = true;
      this.classList.add('is-marquee');
      this.setupMarqueeListeners();

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          this.isVisible = entries[entries.length - 1].isIntersecting;
          this.requestLoop();
        },
        { rootMargin: '120px 0px' }
      );
      this.intersectionObserver.observe(this);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);

      document.addEventListener('visibilitychange', this.onVisibilityChange);

      // Measure on the next frame, never synchronously. A `defer` script can run
      // before the section stylesheet has been applied, and card geometry read
      // at that point is meaningless - the cards have not been given their
      // height or aspect ratio yet, so the loop width and the clone count would
      // both be wrong.
      requestAnimationFrame(() => {
        if (!this.isMarquee) return;
        this.measure();
        this.requestLoop();
      });
    }

    onMotionPreferenceChange(event) {
      if (event.matches) {
        this.intersectionObserver?.disconnect();
        this.resizeObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.enableStaticMode();
      } else {
        this.enableMarqueeMode();
      }
    }

    /* ------------------------------------------------------------ measuring */

    /**
     * Recomputes the loop width from live geometry and tops up the clone count.
     * All reads happen before any write so the browser only lays out once.
     */
    measure() {
      const firstRect = this.originals[0].getBoundingClientRect();
      const lastRect = this.originals[this.originals.length - 1].getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const viewportWidth = this.viewport.clientWidth;

      // Rects share the same transform, so their difference is exact and
      // unaffected by the current offset.
      this.loopWidth = lastRect.right - firstRect.left + gap;
      if (!(this.loopWidth > 0) || !viewportWidth) return;

      const requiredCopies = Math.min(MAX_LOOP_COPIES, Math.max(2, Math.ceil(1 + viewportWidth / this.loopWidth)));
      const currentCopies = 1 + this.track.querySelectorAll('.is-clone').length / this.originals.length;
      if (currentCopies >= requiredCopies) return;

      const fragment = document.createDocumentFragment();
      for (let copy = currentCopies; copy < requiredCopies; copy += 1) {
        this.originals.forEach((item) => {
          const clone = item.cloneNode(true);
          neutraliseClone(clone);
          fragment.appendChild(clone);
        });
      }
      this.track.appendChild(fragment);
    }

    onResize() {
      if (this.resizeFrameId) return;
      this.resizeFrameId = requestAnimationFrame(() => {
        this.resizeFrameId = null;
        if (!this.isMarquee) return;
        this.measure();
        this.render();
      });
    }

    removeClones() {
      this.track.querySelectorAll('.is-clone').forEach((clone) => clone.remove());
    }

    /* --------------------------------------------------------------- looping */

    get shouldRun() {
      if (!this.isMarquee || document.hidden || this.isVisible === false) return false;
      if (this.isDragging || this.momentum !== 0) return true;
      return this.autoScrollEnabled && this.speed > 0 && this.pauseReasons.size === 0;
    }

    requestLoop() {
      if (this.frameId !== null || !this.shouldRun) return;
      this.lastFrameTime = performance.now();
      this.classList.add('is-running');
      this.frameId = requestAnimationFrame(this.tick);
    }

    stopLoop() {
      if (this.frameId !== null) cancelAnimationFrame(this.frameId);
      this.frameId = null;
      this.classList.remove('is-running');
    }

    tick(now) {
      // Clamped so a backgrounded tab cannot teleport the track on resume.
      const elapsed = Math.min(now - this.lastFrameTime, MAX_FRAME_MS);
      this.lastFrameTime = now;

      if (this.momentum !== 0) {
        this.offset += this.momentum * elapsed;
        this.momentum *= Math.pow(FRICTION_PER_FRAME, elapsed / REFERENCE_FRAME_MS);
        if (Math.abs(this.momentum) < MIN_FLICK_PX_PER_MS) this.momentum = 0;
      }

      if (!this.isDragging && this.autoScrollEnabled && this.pauseReasons.size === 0) {
        this.offset += this.speed * this.directionSign * elapsed;
      }

      this.render();

      if (this.shouldRun) {
        this.frameId = requestAnimationFrame(this.tick);
      } else {
        this.frameId = null;
        this.classList.remove('is-running');
      }
    }

    /** Wraps the offset into `[0, loopWidth)` and commits a single transform. */
    render() {
      if (this.loopWidth > 0) {
        this.offset = ((this.offset % this.loopWidth) + this.loopWidth) % this.loopWidth;
      }
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    /**
     * @param {string} reason
     * @param {boolean} paused
     */
    setPaused(reason, paused) {
      if (paused) {
        this.pauseReasons.add(reason);
      } else {
        this.pauseReasons.delete(reason);
      }
      this.requestLoop();
    }

    /* ---------------------------------------------------------------- input */

    setupMarqueeListeners() {
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerEnd, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerEnd, { passive: true });
      // The only non-passive listener in this component. `preventDefault()` is
      // called exclusively for horizontally dominant deltas, so vertical page
      // scrolling is never intercepted.
      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.viewport.addEventListener('click', this.onClickCapture, true);
      this.viewport.addEventListener('dragstart', this.onDragStart);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('pointerenter', this.onPointerEnter, { passive: true });
      this.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
    }

    teardownMarqueeListeners() {
      if (!this.viewport) return;
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerEnd);
      this.viewport.removeEventListener('pointercancel', this.onPointerEnd);
      this.viewport.removeEventListener('wheel', this.onWheel);
      this.viewport.removeEventListener('click', this.onClickCapture, true);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
    }

    onDragStart(event) {
      event.preventDefault();
    }

    onPointerEnter(event) {
      if (this.pauseOnHover && event.pointerType === 'mouse' && this.hoverQuery.matches) {
        this.setPaused('hover', true);
      }
    }

    onPointerLeave() {
      this.setPaused('hover', false);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.isPointerDown = true;
      this.pointerId = event.pointerId;
      this.lastPointerX = event.clientX;
      this.dragDistance = 0;
      this.samples.length = 0;
      this.samples.push({ x: event.clientX, t: event.timeStamp });

      if (this.touchResumeTimer) {
        clearTimeout(this.touchResumeTimer);
        this.touchResumeTimer = null;
      }
      if (this.pauseOnTouch && event.pointerType !== 'mouse') {
        this.setPaused('touch', true);
      }
    }

    onPointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.lastPointerX;
      this.lastPointerX = event.clientX;
      this.dragDistance += Math.abs(deltaX);

      if (!this.isDragging) {
        if (this.dragDistance < DRAG_THRESHOLD_PX) return;
        this.isDragging = true;
        this.classList.add('is-dragging');
        try {
          this.viewport.setPointerCapture(this.pointerId);
        } catch (error) {
          // Capture is a progressive enhancement; dragging still works without it.
        }
      }

      this.momentum = 0;
      this.offset -= deltaX;
      this.samples.push({ x: event.clientX, t: event.timeStamp });
      if (this.samples.length > 12) this.samples.shift();
      this.requestLoop();
    }

    onPointerEnd(event) {
      if (event.pointerId !== this.pointerId) return;

      const wasDragging = this.isDragging;
      const travelled = this.dragDistance;

      try {
        this.viewport.releasePointerCapture(this.pointerId);
      } catch (error) {
        // Nothing to release.
      }

      this.isPointerDown = false;
      this.isDragging = false;
      this.pointerId = null;
      this.classList.remove('is-dragging');

      if (wasDragging && event.type === 'pointerup') {
        this.momentum = -this.releaseVelocity();
      }
      if (travelled > CLICK_SUPPRESS_PX) {
        this.suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
      }

      if (this.pauseReasons.has('touch')) {
        this.touchResumeTimer = setTimeout(() => {
          this.touchResumeTimer = null;
          this.setPaused('touch', false);
        }, TOUCH_RESUME_DELAY_MS);
      }

      this.requestLoop();
    }

    /**
     * Average pointer velocity over the last ~90ms, in px/ms.
     * @returns {number}
     */
    releaseVelocity() {
      if (this.samples.length < 2) return 0;
      const last = this.samples[this.samples.length - 1];
      let reference = this.samples[0];
      for (let index = this.samples.length - 1; index >= 0; index -= 1) {
        if (last.t - this.samples[index].t > VELOCITY_WINDOW_MS) break;
        reference = this.samples[index];
      }
      const duration = last.t - reference.t;
      if (duration <= 0) return 0;
      const velocity = (last.x - reference.x) / duration;
      return Math.max(-MAX_FLICK_PX_PER_MS, Math.min(MAX_FLICK_PX_PER_MS, velocity));
    }

    onWheel(event) {
      const deltaX = wheelDeltaToPixels(event, this.viewport.clientWidth);
      if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

      event.preventDefault();
      this.momentum = 0;
      this.offset += deltaX;
      this.requestLoop();
    }

    onClickCapture(event) {
      if (performance.now() >= this.suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }

    /* ------------------------------------------------------------- keyboard */

    onFocusIn(event) {
      this.setPaused('focus', true);
      const card = event.target.closest('.m-review-gallery__card');
      if (card) this.bringIntoView(card);
    }

    onFocusOut() {
      // Fires before focus lands, so defer the containment check by a task.
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.setPaused('focus', false);
      }, 0);
    }

    onKeyDown(event) {
      if (!this.isMarquee) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const step = this.originals[0].getBoundingClientRect().width + this.currentGap();
      this.momentum = 0;
      this.offset += event.key === 'ArrowRight' ? step : -step;
      this.requestLoop();
    }

    currentGap() {
      return parseFloat(getComputedStyle(this.track).columnGap) || 0;
    }

    /**
     * Nudges the track so a focused card sits fully inside the viewport.
     * @param {HTMLElement} card
     */
    bringIntoView(card) {
      if (!this.isMarquee) return;
      const viewportRect = this.viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      let delta = 0;

      if (cardRect.left < viewportRect.left + FOCUS_EDGE_PADDING_PX) {
        delta = cardRect.left - (viewportRect.left + FOCUS_EDGE_PADDING_PX);
      } else if (cardRect.right > viewportRect.right - FOCUS_EDGE_PADDING_PX) {
        delta = cardRect.right - (viewportRect.right - FOCUS_EDGE_PADDING_PX);
      }
      if (!delta) return;

      this.momentum = 0;
      this.offset += delta;
      this.render();
    }

    /* --------------------------------------------------------------- viewer */

    onViewerOpen() {
      this.setPaused('lightbox', true);
    }

    onViewerClose() {
      this.setPaused('lightbox', false);
    }

    onVisibilityChange() {
      this.requestLoop();
    }
  }

  customElements.define('m-review-gallery', ReviewGallery);
}
